import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Users, Search, Sparkles, ArrowLeft, ExternalLink, Link2, GraduationCap } from "lucide-react";
import { api, signedFileUrl } from "@/lib/api";
import { brand } from "@/tokens/brandTokens";
import { Section, Chip, StatusBadge } from "@/components/ui-brand";
import { PanelHeader, Tabs, TagInput, Field, Card } from "@/components/panel";
import { CierreDialog, ESTADOS_CON_CIERRE } from "@/components/empresa/CierreDialog";
import { EmpresaRubro } from "@/components/empresa/EmpresaRubro";

const PIPE_COLS = [
  { key: "enviada", label: "Recibidas" },
  { key: "vista", label: "Vista" },
  { key: "contactado", label: "Contactado" },
  { key: "contratado", label: "Contratado" },
  { key: "descartada", label: "No avanza" },
];

const SignedImg = ({ path, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { let ok = true; signedFileUrl(path).then((u) => ok && setSrc(u)).catch(() => {}); return () => { ok = false; }; }, [path]);
  return <div className="aspect-square rounded-xl overflow-hidden border-2 border-[var(--brand-border)] bg-slate-50">{src ? <img src={src} alt={alt} className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-slate-100" />}</div>;
};

const PortfolioView = ({ portfolio = [] }) => {
  const imgs = portfolio.filter((p) => p.storage_path);
  const links = portfolio.filter((p) => !p.storage_path);
  if (portfolio.length === 0) return <p className="text-sm text-[var(--brand-text-muted)]">Sin portfolio.</p>;
  return (
    <div>
      {imgs.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4" data-testid="perfil-portfolio-images">
          {imgs.map((p, i) => <SignedImg key={i} path={p.storage_path} alt={p.titulo || "portfolio"} />)}
        </div>
      )}
      {links.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-1.5 text-sm text-[var(--brand-primary)]" data-testid={`perfil-portfolio-link-${i}`}>
          {p.tipo === "link" ? <Link2 size={14} /> : <ExternalLink size={14} />}{p.titulo || p.url}<span className="chip">{p.tipo}</span>
        </a>
      ))}
    </div>
  );
};

const TalentoDetail = ({ t, onBack }) => (
  <div>
    <button onClick={onBack} className="btn-outline text-sm mb-4 inline-flex items-center gap-1.5" data-testid="talento-back"><ArrowLeft size={15} /> Volver</button>
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-1">
        <div className="flex items-center gap-2"><h3 className="font-heading font-bold text-xl text-[var(--brand-primary)]" data-testid="talento-detail-name">{t.name}</h3>{t.destacado && <Chip accent testid="talento-detail-destacado">★ Destacado</Chip>}</div>
        <p className="text-xs text-[var(--brand-text-muted)] mt-1">{t.ubicacion} · {t.marca}</p>
        {t.bio_publica && <p className="text-sm text-[var(--brand-primary)] mt-3">{t.bio_publica}</p>}
        {t.skills_hard?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-4">{t.skills_hard.map((s) => <Chip key={s.nombre}>{s.nombre}</Chip>)}</div>}
        {t.fortalezas_visibles?.length > 0 && (
          <div className="mt-3"><span className="text-xs font-semibold text-[var(--brand-accent)]">Fortalezas:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">{t.fortalezas_visibles.map((f, i) => <Chip key={i} accent>{f}</Chip>)}</div></div>
        )}
        {t.formacion?.length > 0 && (
          <div className="mt-4 space-y-1">{t.formacion.map((f, i) => <p key={i} className="text-xs text-[var(--brand-text-muted)] flex items-center gap-1.5"><GraduationCap size={13} />{f.programa} · {f.institucion}</p>)}</div>
        )}
        {t.linkedin && <a href={t.linkedin} target="_blank" rel="noreferrer" className="text-sm text-[var(--brand-accent)] mt-3 inline-flex items-center gap-1.5"><Link2 size={14} /> LinkedIn</a>}
      </Card>
      <Card className="lg:col-span-2">
        <h4 className="font-heading font-bold text-[var(--brand-primary)] mb-3">Portfolio</h4>
        <PortfolioView portfolio={t.portfolio} />
      </Card>
    </div>
  </div>
);

export default function EmpresaPanel() {
  const [tab, setTab] = useState("busquedas");
  const [empresa, setEmpresa] = useState(null);
  const [busquedas, setBusquedas] = useState([]);
  const [talento, setTalento] = useState([]);
  const [selected, setSelected] = useState(null);
  const [posts, setPosts] = useState([]);
  const [catalogs, setCatalogs] = useState({ skill_hard: [], skill_soft: [], formacion: [], rubros: [], rubro_otro: "" });
  const [nueva, setNueva] = useState({ titulo: "", descripcion: "", formacion_requerida: "", skills_requeridas_hard: [], skills_requeridas_soft: [], ubicacion: "", modalidad: "Presencial", marca: "Córdoba Crea Futuro" });
  const [tf, setTf] = useState({ q: "", ubicacion: "", skill: "", formacion: "" });
  const [aiQuery, setAiQuery] = useState("");
  const [aiFiltros, setAiFiltros] = useState(null);
  const [talentoSel, setTalentoSel] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [dragPid, setDragPid] = useState(null);
  const [cierre, setCierre] = useState(null);
  const aiBuscar = async () => {
    if (!aiQuery.trim()) return;
    try {
      const r = await api.post("/empresa/talento/ai", { consulta: aiQuery });
      setTalento(r.data.items); setAiFiltros(r.data.filtros);
      toast.success("Búsqueda interpretada por IA");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo interpretar"); }
  };

  const loadBusquedas = () => api.get("/empresa/busquedas").then((r) => setBusquedas(r.data.items));
  const loadTalento = () => api.get(`/empresa/talento?q=${tf.q}&ubicacion=${tf.ubicacion}&skill=${tf.skill}&formacion=${tf.formacion}`).then((r) => setTalento(r.data.items));
  const loadPipeline = () => api.get("/empresa/postulaciones?limit=100").then((r) => setPipeline(r.data.items));
  const verTalento = async (id) => { try { const r = await api.get(`/empresa/talento/${id}`); setTalentoSel(r.data); } catch { toast.error("Perfil no disponible"); } };
  const guardarEstado = async (pid, estado, extra = {}) => {
    try {
      await api.put(`/postulaciones/${pid}/estado`, { estado, ...extra });
      setPipeline((prev) => prev.map((p) => (p.id === pid ? { ...p, estado } : p)));
      toast.success(`Estado: ${PIPE_COLS.find((c) => c.key === estado)?.label || estado}`);
      if (selected) verPosts(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); loadPipeline(); }
  };
  const pedirEstado = (pid, estado) => (ESTADOS_CON_CIERRE.includes(estado) ? setCierre({ pid, estado }) : guardarEstado(pid, estado));
  const moverEtapa = (pid, estado) => {
    if (pipeline.find((p) => p.id === pid)?.estado === estado) return;
    pedirEstado(pid, estado);
  };

  useEffect(() => {
    api.get("/empresa/profile").then((r) => { setEmpresa(r.data); setNueva((n) => ({ ...n, marca: r.data.marca })); });
    api.get("/catalogs").then((r) => setCatalogs(r.data));
    loadBusquedas(); loadTalento(); loadPipeline();
    // eslint-disable-next-line
  }, []);

  const publicar = async () => {
    if (!nueva.titulo || !nueva.descripcion) return toast.error("Completá título y descripción");
    await api.post("/empresa/busquedas", nueva); toast.success("Búsqueda publicada");
    setNueva({ titulo: "", descripcion: "", formacion_requerida: "", skills_requeridas_hard: [], skills_requeridas_soft: [], ubicacion: "", modalidad: "Presencial", marca: empresa.marca });
    setTab("busquedas"); loadBusquedas();
  };
  const verPosts = async (b) => { setSelected(b); const r = await api.get(`/empresa/busquedas/${b.id}/postulaciones`); setPosts(r.data.items); };
  const cerrar = async (id) => { await api.put(`/empresa/busquedas/${id}/cerrar`); toast.success("Búsqueda cerrada"); loadBusquedas(); };

  const tabs = [
    { key: "busquedas", label: "Mis Búsquedas" },
    { key: "talento", label: "Buscar Talento" },
    { key: "pipeline", label: "Tus postulantes" },
    { key: "perfil", label: "Perfil" },
  ];

  if (!empresa) return <div className="py-20 text-center font-heading text-[var(--brand-primary)]">Cargando…</div>;

  return (
    <div>
      <PanelHeader title={empresa.razon_social} subtitle={(empresa.rubro === catalogs.rubro_otro ? empresa.rubro_detalle : empresa.rubro) || "Empresa"}
        right={<button onClick={() => setTab("publicar")} className="btn-accent flex items-center gap-2" data-testid="header-publicar"><Plus size={16} /> Nueva búsqueda</button>} />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <Section className="py-8">
        {tab === "busquedas" && !selected && (
          <div className="grid md:grid-cols-2 gap-5">
            {busquedas.map((b) => (
              <Card key={b.id} data-testid={`mibusqueda-${b.id}`}>
                <div className="flex justify-between items-start"><h3 className="font-heading font-bold text-[var(--brand-primary)]">{b.titulo}</h3><StatusBadge estado={b.estado} /></div>
                <p className="text-xs text-[var(--brand-text-muted)] mt-1">{b.ubicacion} · {b.modalidad}</p>
                <p className="text-sm mt-3 flex items-center gap-2 text-[var(--brand-primary)]"><Users size={15} className="text-[var(--brand-accent)]" /> {b.postulaciones_count} postulaciones</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => verPosts(b)} className="btn-primary text-sm" data-testid={`ver-posts-${b.id}`}>Ver postulantes</button>
                  {b.estado === "abierta" && <button onClick={() => cerrar(b.id)} className="btn-outline text-sm" data-testid={`cerrar-${b.id}`}>Cerrar</button>}
                </div>
              </Card>
            ))}
            {busquedas.length === 0 && <p className="text-[var(--brand-text-muted)]">Todavía no publicaste búsquedas.</p>}
          </div>
        )}

        {tab === "busquedas" && selected && (
          <div>
            <button onClick={() => setSelected(null)} className="btn-outline text-sm mb-4" data-testid="back-busquedas">← Volver</button>
            <h3 className="font-heading font-bold text-xl text-[var(--brand-primary)] mb-4">Postulantes · {selected.titulo}</h3>
            <div className="space-y-4">
              {posts.map((p) => (
                <Card key={p.id} data-testid={`postulante-${p.id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-3">
                    <div>
                      <div className="font-heading font-bold text-[var(--brand-primary)]">{p.perfil?.name}</div>
                      <div className="text-xs text-[var(--brand-text-muted)]">{p.perfil?.ubicacion} · {p.perfil?.marca}</div>
                    </div>
                    <StatusBadge estado={p.estado} label={p.estado === "descartada" ? "No avanza" : undefined} testid={`postulante-estado-${p.id}`} />
                  </div>
                  {p.perfil?.bio_publica && <p className="text-sm text-[var(--brand-primary)] mt-2">{p.perfil.bio_publica}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">{(p.perfil?.skills_hard || []).map((s) => <Chip key={s.nombre}>{s.nombre}</Chip>)}</div>
                  {p.perfil?.fortalezas_visibles?.length > 0 && (
                    <div className="mt-3"><span className="text-xs font-semibold text-[var(--brand-accent)]">Fortalezas:</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">{p.perfil.fortalezas_visibles.map((f, i) => <Chip key={i} accent>{f}</Chip>)}</div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {["vista", "contactado", "contratado", "descartada"].map((e) => (
                      <button key={e} onClick={() => pedirEstado(p.id, e)} className="btn-outline text-xs" data-testid={`estado-${e}-${p.id}`}>{e === "descartada" ? "No avanza" : `Marcar ${e}`}</button>
                    ))}
                  </div>
                </Card>
              ))}
              {posts.length === 0 && <p className="text-[var(--brand-text-muted)]">Sin postulantes aún.</p>}
            </div>
          </div>
        )}

        {tab === "publicar" && (
          <Card className="max-w-2xl">
            <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-4">Nueva búsqueda laboral</h3>
            <div className="space-y-3">
              <Field label="Título" value={nueva.titulo} onChange={(e) => setNueva({ ...nueva, titulo: e.target.value })} data-testid="nueva-titulo" />
              <Field label="Descripción" textarea value={nueva.descripcion} onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })} data-testid="nueva-desc" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ubicación" value={nueva.ubicacion} onChange={(e) => setNueva({ ...nueva, ubicacion: e.target.value })} data-testid="nueva-ubic" />
                <label className="block"><span className="text-xs font-semibold text-[var(--brand-primary)]">Modalidad</span>
                  <select value={nueva.modalidad} onChange={(e) => setNueva({ ...nueva, modalidad: e.target.value })} data-testid="nueva-modalidad" className="mt-1 w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm">
                    {["Presencial", "Híbrido", "Remoto"].map((m) => <option key={m}>{m}</option>)}
                  </select>
                </label>
              </div>
              <label className="block"><span className="text-xs font-semibold text-[var(--brand-primary)]">Formación requerida</span>
                <input list="prog-req" value={nueva.formacion_requerida} onChange={(e) => setNueva({ ...nueva, formacion_requerida: e.target.value })} data-testid="nueva-formacion" className="mt-1 w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm outline-none focus:border-[var(--brand-accent)]" />
                <datalist id="prog-req">{catalogs.formacion.map((p) => <option key={p} value={p} />)}</datalist>
              </label>
              <div><span className="text-xs font-semibold text-[var(--brand-primary)]">Skills hard requeridas</span>
                <div className="mt-1"><TagInput tags={nueva.skills_requeridas_hard} suggestions={catalogs.skill_hard} testid="nueva-hard" onChange={(t) => setNueva({ ...nueva, skills_requeridas_hard: t })} /></div></div>
              <div><span className="text-xs font-semibold text-[var(--brand-primary)]">Skills soft requeridas</span>
                <div className="mt-1"><TagInput tags={nueva.skills_requeridas_soft} suggestions={catalogs.skill_soft} testid="nueva-soft" onChange={(t) => setNueva({ ...nueva, skills_requeridas_soft: t })} /></div></div>
              <button onClick={publicar} className="btn-primary" data-testid="publicar-submit">Publicar búsqueda</button>
            </div>
          </Card>
        )}

        {tab === "talento" && !talentoSel && (
          <div>
            <div className="card-brand p-5 mb-5" style={{ background: "var(--brand-accent-light, #f0f9ff)" }} data-testid="talento-ai-card">
              <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[var(--brand-accent)]" /><span className="font-heading font-bold text-[var(--brand-primary)]">Buscar con IA</span></div>
              <p className="text-sm text-[var(--brand-text-muted)] mb-3">Describí en lenguaje natural qué perfil buscás. La IA lo traduce a filtros reales y consulta la base — nunca inventa perfiles.</p>
              <div className="flex gap-2">
                <input value={aiQuery} onChange={(e) => setAiQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && aiBuscar()} data-testid="talento-ai-input"
                  placeholder="Ej: alguien de marketing digital en Villa María con experiencia en IA" className="flex-1 rounded-full border-2 border-[var(--brand-border)] px-4 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
                <button onClick={aiBuscar} className="btn-accent flex items-center gap-1.5" data-testid="talento-ai-search"><Sparkles size={15} /> Buscar</button>
              </div>
              {aiFiltros && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {Object.entries(aiFiltros).filter(([, v]) => v).map(([k, v]) => <Chip key={k} accent testid={`ai-filtro-${k}`}>{k}: {v}</Chip>)}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="flex items-center gap-2 rounded-full border-2 border-[var(--brand-border)] px-4 py-2 flex-1 min-w-[200px]">
                <Search size={16} className="text-[var(--brand-text-muted)]" />
                <input placeholder="Nombre o skill…" value={tf.q} onChange={(e) => setTf({ ...tf, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadTalento()} data-testid="talento-q" className="flex-1 text-sm outline-none" />
              </div>
              <input placeholder="Ubicación" value={tf.ubicacion} onChange={(e) => setTf({ ...tf, ubicacion: e.target.value })} data-testid="talento-ubic" className="rounded-full border-2 border-[var(--brand-border)] px-4 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
              <input placeholder="Formación" value={tf.formacion} onChange={(e) => setTf({ ...tf, formacion: e.target.value })} data-testid="talento-form" className="rounded-full border-2 border-[var(--brand-border)] px-4 py-2 text-sm outline-none focus:border-[var(--brand-accent)]" />
              <button onClick={loadTalento} className="btn-primary" data-testid="talento-buscar">Buscar</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {talento.map((t) => (
                <Card key={t.id} data-testid={`talento-${t.id}`}>
                  <div className="flex items-center gap-2"><h3 className="font-heading font-bold text-[var(--brand-primary)]">{t.name}</h3>{t.destacado && <Chip accent testid={`destacado-${t.id}`}>★ Destacado</Chip>}</div>
                  <p className="text-xs text-[var(--brand-text-muted)]">{t.ubicacion} · {t.marca}</p>
                  <p className="text-sm text-[var(--brand-primary)] mt-2 line-clamp-3">{t.bio_publica}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">{(t.skills_hard || []).slice(0, 4).map((s) => <Chip key={s.nombre}>{s.nombre}</Chip>)}</div>
                  {t.fortalezas_visibles?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{t.fortalezas_visibles.slice(0, 2).map((f, i) => <Chip key={i} accent>{f}</Chip>)}</div>}
                  <button onClick={() => verTalento(t.id)} className="btn-outline text-xs mt-4" data-testid={`ver-perfil-${t.id}`}>Ver perfil completo</button>
                </Card>
              ))}
              {talento.length === 0 && <p className="text-[var(--brand-text-muted)] col-span-full">No se encontraron perfiles.</p>}
            </div>
          </div>
        )}

        {tab === "talento" && talentoSel && (
          <TalentoDetail t={talentoSel} onBack={() => setTalentoSel(null)} />
        )}

        {tab === "pipeline" && (
          <div>
            <p className="text-sm text-[var(--brand-text-muted)] mb-4">Tablero de todos tus postulantes. Arrastrá cada tarjeta entre etapas; el cambio se guarda y se le notifica al egresado/a.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kanban-board">
              {PIPE_COLS.map((col) => {
                const cards = pipeline.filter((p) => (p.estado || "enviada") === col.key);
                return (
                  <div key={col.key} data-testid={`kanban-col-${col.key}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => dragPid && moverEtapa(dragPid, col.key)}
                    className="rounded-xl bg-[var(--brand-surface)] p-3 min-h-[220px]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-heading font-bold text-sm text-[var(--brand-primary)]">{col.label}</span>
                      <span className="text-xs rounded-full px-2 py-0.5 text-white" style={{ background: `var(--status-${col.key})` }}>{cards.length}</span>
                    </div>
                    <div className="space-y-2">
                      {cards.map((p) => (
                        <div key={p.id} draggable onDragStart={() => setDragPid(p.id)} onDragEnd={() => setDragPid(null)}
                          data-testid={`kanban-card-${p.id}`}
                          className="bg-white rounded-lg border border-[var(--brand-border)] p-3 shadow-sm"
                          style={{ cursor: "grab", opacity: dragPid === p.id ? 0.4 : 1 }}>
                          <div className="font-semibold text-sm text-[var(--brand-primary)]">{p.egresado_nombre || p.perfil?.name}</div>
                          <div className="text-xs text-[var(--brand-text-muted)] mt-0.5 truncate">{p.busqueda_titulo}</div>
                        </div>
                      ))}
                      {cards.length === 0 && <p className="text-xs text-[var(--brand-text-muted)] text-center py-4">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "perfil" && (
          <Card className="max-w-2xl">
            <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-4">Datos de la empresa</h3>
            <div className="space-y-3">
              <Field label="Razón social" defaultValue={empresa.razon_social} onBlur={(e) => api.put("/empresa/profile", { razon_social: e.target.value }).then(() => toast.success("Guardado"))} data-testid="emp-razon" />
              <EmpresaRubro empresa={empresa} rubros={catalogs.rubros} otro={catalogs.rubro_otro} onSaved={(e) => setEmpresa((prev) => ({ ...prev, rubro: e.rubro, rubro_detalle: e.rubro_detalle }))} />
              <Field label="Nombre de contacto" defaultValue={empresa.contacto_nombre} onBlur={(e) => api.put("/empresa/profile", { contacto_nombre: e.target.value })} data-testid="emp-contacto-nombre" />
              <Field label="Email de contacto" defaultValue={empresa.contacto_email} onBlur={(e) => api.put("/empresa/profile", { contacto_email: e.target.value })} data-testid="emp-contacto-email" />
              <Field label="Teléfono de contacto" defaultValue={empresa.contacto_telefono} onBlur={(e) => api.put("/empresa/profile", { contacto_telefono: e.target.value })} data-testid="emp-contacto-tel" />
              <Field label="CUIT" defaultValue={empresa.cuit} onBlur={(e) => api.put("/empresa/profile", { cuit: e.target.value }).then(() => toast.success("Guardado"))} data-testid="emp-cuit" />
              <Field label="LinkedIn" defaultValue={empresa.linkedin} onBlur={(e) => api.put("/empresa/profile", { linkedin: e.target.value }).then(() => toast.success("Guardado"))} placeholder="https://linkedin.com/company/…" data-testid="emp-linkedin" />
            </div>
          </Card>
        )}
      </Section>
      <CierreDialog pending={cierre} onCancel={() => setCierre(null)}
        onConfirm={(extra) => { const c = cierre; setCierre(null); guardarEstado(c.pid, c.estado, extra); }} />
    </div>
  );
}
